import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from './firebase';
import { doc, setDoc } from 'firebase/firestore';
import useAuth from './useAuth';

const OnboardingPage = () => {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const [handle, setHandle] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [bio, setBio] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!loading) {
      if (!user) {
        setError('Please sign in first.');
      } else if (profile) {
        navigate('/');
      } else if (user.displayName && !displayName) {
        setDisplayName(user.displayName);
      }
    }
  }, [user, profile, loading, navigate, displayName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    if (!handle || !displayName) {
      setError('Handle and Display Name are required.');
      setIsSubmitting(false);
      return;
    }

    try {
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        handle,
        displayName,
        profilePreferredName: preferredName || displayName.split(' ')[0] || displayName,
        profileName: displayName,
        bio,
        createdAt: new Date().toISOString(),
        reputation: 0,
        publisherStatus: 'standard',
        developerVerification: false,
        email: user.email,
        emailVerified: user.emailVerified || false,
      });
      navigate('/');
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Failed to save profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return (
    <div style={{ background: '#0f172a', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: '#64748b' }}>Loading...</p>
    </div>
  );

  if (!user) return (
    <div style={{ background: '#0f172a', minHeight: '100vh', padding: 20, color: '#f8fafc' }}>
      {error || 'You must be signed in to view this page.'}
    </div>
  );

  const currentEmail = user.email || '';
  const isRelayEmail = currentEmail.includes('@privaterelay.apple');
  const hasPasswordProvider = user.providerData?.some(p => p.providerId === 'password');

  return (
    <div style={{ background: '#0f172a', minHeight: '100vh', color: '#f8fafc' }}>
      <div style={{ maxWidth: 520, margin: '0 auto', padding: '80px 24px 48px' }}>
        <div style={{
          background: '#1e293b', borderRadius: 16, padding: 32,
          border: '1px solid #334155',
        }}>
          <h2 style={{ margin: '0 0 6px', fontSize: '1.3rem', fontWeight: 700 }}>
            Complete Your Profile
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 24px' }}>
            Welcome to mChatAI! Let's get your profile set up so you can join the community.
          </p>

          {error && (
            <div style={{
              color: '#f87171', fontSize: '0.8rem', marginBottom: 16,
              background: 'rgba(248, 113, 113, 0.08)', padding: '8px 12px', borderRadius: 8,
            }}>{error}</div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: '0.85rem', fontWeight: 500 }}>Display Name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Jane Doe"
                style={inputStyle}
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: '0.85rem', fontWeight: 500 }}>What should we call you?</label>
              <input
                type="text"
                value={preferredName}
                onChange={(e) => setPreferredName(e.target.value)}
                placeholder={displayName ? displayName.split(' ')[0] : 'e.g., Joe'}
                style={inputStyle}
              />
              <p style={{ color: '#64748b', fontSize: '0.72rem', margin: '4px 0 0' }}>
                Used by the AI Wizard, games, and throughout the app. Defaults to your first name.
              </p>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: '0.85rem', fontWeight: 500 }}>Handle</label>
              <input
                type="text"
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                placeholder="janedoe"
                style={inputStyle}
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: '0.85rem', fontWeight: 500 }}>Bio (Optional)</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell us a little about yourself..."
                rows="3"
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>

            {/* Cross-platform identity section */}
            <div style={{
              background: '#0f172a', borderRadius: 10, padding: '14px 16px',
              border: '1px solid #334155',
            }}>
              <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Your mChatAI Identity
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                </svg>
                <span style={{ color: '#f8fafc', fontSize: '0.85rem' }}>{currentEmail}</span>
              </div>
              {isRelayEmail ? (
                <p style={{ color: '#f59e0b', fontSize: '0.78rem', margin: '0 0 4px' }}>
                  Your Apple ID uses a private relay email. After setup, visit your Account page to add a real email for cross-platform sign-in.
                </p>
              ) : (
                <p style={{ color: '#64748b', fontSize: '0.78rem', margin: '0 0 4px' }}>
                  This email is your mChatAI identity across all platforms.
                </p>
              )}
              {!hasPasswordProvider && (
                <p style={{ color: '#64748b', fontSize: '0.75rem', margin: 0, fontStyle: 'italic' }}>
                  After setup, visit your Account page to add a password for cross-platform sign-in.
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                background: '#3b82f6', color: '#fff', border: 'none', padding: '12px 20px',
                borderRadius: 10, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
                fontFamily: 'inherit', opacity: isSubmitting ? 0.6 : 1,
              }}
            >
              {isSubmitting ? 'Saving...' : 'Save Profile'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1px solid #334155', background: '#0f172a', color: '#f8fafc',
  fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
};

export default OnboardingPage;
